import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import SEO from '../components/SEO';
import LoginModal from '../components/LoginModal';
import RegisterModal from '../components/RegisterModal';
import DirectoryCard from '../components/DirectoryCard';
import OrganizationCard from '../components/OrganizationCard';
import HeadlineCard from '../components/HeadlineCard';
import BillLifecycleProgress from '../components/BillLifecycleProgress';
import '../pages/legislation.css';
import '../pages/billProfile.css';
import '../pages/dashboard.css';
import './overview.css';

/* ── Reveal-on-scroll wrapper ───────────────────────────────
   Plain IntersectionObserver, no library — this repo has none.
   Falls back to always-visible if IO is unsupported. */
function Reveal({ children, className = '' }) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;
        if (typeof IntersectionObserver === 'undefined') {
            setVisible(true);
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.unobserve(node);
                }
            },
            { threshold: 0.15 }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={ref} className={`reveal ${visible ? 'is-visible' : ''} ${className}`}>
            {children}
        </div>
    );
}

function DataBadge({ live }) {
    return live
        ? <span className="overview-data-badge overview-data-badge--live"><i className="bi bi-broadcast me-1" />Live Data</span>
        : <span className="overview-data-badge"><i className="bi bi-info-circle me-1" />Sample Data</span>;
}

/* ── Fallbacks (shown until real data loads, or if a fetch fails) ── */
const FALLBACK_BILLS = [
    {
        bill_id: 'sample-1', bill_type: 'AB', bill_number: '000', session_number: 0, house: 'Assembly',
        title: 'An act relating to a sample bill, illustrating how tracked legislation appears on your dashboard.',
        status: 'In Committee', location: 'Assembly Committee on Sample Policy',
        ai_summary: 'This sample bill would establish a framework for illustrating how AI-generated bill summaries appear alongside real legislative text.',
        LatestVersion: { BillVersionAuthors: [{ author_name: 'Sample Assemblymember', author_type: 'Legislator', primary_author_flag: 'Y' }] },
    },
    {
        bill_id: 'sample-2', bill_type: 'SB', bill_number: '000', session_number: 0, house: 'Senate',
        title: 'An act relating to a second sample bill, showing status and committee location at a glance.',
        status: 'Passed Committee', location: 'Senate Floor',
        LatestVersion: { BillVersionAuthors: [{ author_name: 'Sample Senator', author_type: 'Legislator', primary_author_flag: 'Y' }] },
    },
    {
        bill_id: 'sample-3', bill_type: 'AB', bill_number: '001', session_number: 0, house: 'Assembly',
        title: 'An act relating to a third sample bill, demonstrating how amendments and votes are tracked over time.',
        status: 'Chaptered', location: 'Chaptered',
        LatestVersion: { BillVersionAuthors: [{ author_name: 'Sample Assemblymember', author_type: 'Legislator', primary_author_flag: 'Y' }] },
    },
];

const FALLBACK_HISTORY = [
    { bill_history_id: 1, action_date: '2026-01-12', action: 'Introduced. Read first time.', primary_location: 'Assembly', secondary_location: null, ternary_location: null, action_sequence: 1 },
    { bill_history_id: 2, action_date: '2026-02-03', action: 'Referred to Com. on Sample Policy.', primary_location: 'Assembly', secondary_location: 'Committee', ternary_location: 'CX00', action_sequence: 2 },
    { bill_history_id: 3, action_date: '2026-03-18', action: 'From committee: Do pass.', primary_location: 'Assembly', secondary_location: 'Committee', ternary_location: 'CX00', action_sequence: 3 },
];

const FALLBACK_HEADLINES = [
    { publication: 'Sample Capitol Journal', title: 'Lawmakers Advance Sample Policy Package', snippet: 'A short excerpt illustrating how curated policy headlines appear in your daily Public Square feed.' },
    { publication: 'Sample Sacramento Wire', title: 'Committee Schedules Hearing on Sample Bill', snippet: 'Another example headline, showing publication name, title, and a brief snippet.' },
];

const SAMPLE_PEOPLE = [
    { profile_id: 'sample-1', firstName: 'Jordan', lastName: 'Alvarez', title: 'Legislative Director', organization: 'Office of a Sample Assemblymember' },
    { profile_id: 'sample-2', firstName: 'Priya', lastName: 'Nair', title: 'Government Affairs Manager', organization: 'Sample Policy Coalition' },
    { profile_id: 'sample-3', firstName: 'Marcus', lastName: 'Chen', title: 'Chief Consultant', organization: 'Assembly Committee on Sample Policy' },
    { profile_id: 'sample-4', firstName: 'Elena', lastName: 'Whitfield', title: 'Principal Consultant', organization: 'Senate Committee on Sample Policy' },
    { profile_id: 'sample-5', firstName: 'Devon', lastName: 'Brooks', title: 'Lobbyist', organization: 'California Sample Advocates Association' },
    { profile_id: 'sample-6', firstName: 'Amara', lastName: 'Odom', title: 'Deputy Director', organization: 'Sample Department of Policy' },
];

const SAMPLE_ORGS = [
    { organization_id: 'sample-1', organization_name: 'Sample Policy Coalition', street_address: '1100 K Street', city: 'Sacramento', state: 'CA', main_phone: '9165550100' },
    { organization_id: 'sample-2', organization_name: 'California Sample Advocates Association', street_address: '925 L Street', city: 'Sacramento', state: 'CA', main_phone: '9165550101' },
];

const WORKSPACE_TOOLS = [
    {
        icon: 'bi-chat-square-text',
        title: 'Stakeholder Feedback Portals',
        desc: "Send a set of bills to a Client or Stakeholder Group's contacts with a deadline, and their notes come straight back into your Workspace.",
        preview: (
            <div className="overview-mini-quote">
                <i className="bi bi-quote" />
                <p>"This one's a priority for our members — please continue to support."</p>
                <span>— Sample Contact, on AB 000</span>
            </div>
        ),
    },
    {
        icon: 'bi-file-earmark-bar-graph',
        title: 'Client & Team Reports',
        desc: 'Build a bill report for a Client or Stakeholder Group — or for internal use — and send it to recipients who view it with no login required.',
        preview: (
            <div className="overview-mini-report">
                <div><i className="bi bi-file-earmark-pdf me-2" />Weekly Report — Sample Policy Coalition</div>
                <div className="overview-mini-report-row"><span>AB 000</span><span className="text-muted">2 notes</span></div>
                <div className="overview-mini-report-row"><span>SB 000</span><span className="text-muted">1 note</span></div>
            </div>
        ),
    },
    {
        icon: 'bi-person-check',
        title: 'Bill Assignments',
        desc: 'Assign a Lead and other members to any bill your Workspace is tracking, so everyone knows who owns what.',
        preview: (
            <div className="overview-mini-chips">
                <span className="overview-role-chip overview-role-chip--lead">JA</span>
                <span className="overview-role-chip overview-role-chip--monitor">PN</span>
                <span className="text-muted small ms-2">Lead · Monitor on AB 000</span>
            </div>
        ),
    },
    {
        icon: 'bi-list-check',
        title: 'Task Management',
        desc: 'Track every task across every bill your Workspace or its Clients are tracking, in one rolled-up, filterable view.',
        preview: (
            <ul className="overview-mini-tasks">
                <li><span className="overview-task-priority overview-task-priority--high" />Draft support letter <span className="text-muted ms-auto">Mar 4</span></li>
                <li><span className="overview-task-priority overview-task-priority--medium" />Confirm hearing witnesses <span className="text-muted ms-auto">Mar 9</span></li>
            </ul>
        ),
    },
];

const PEOPLE_LIMIT = 6;

function personQualifies(title, groupType) {
    if (!title) return false;
    const t = title.toLowerCase();
    if (t.includes('chief of staff')) return true;
    if (t.includes('senator')) return true;
    if (t.includes('assembly member') || t.includes('assemblymember')) return true;
    if (t.includes('partner') && groupType === 'Lobbyists') return true;
    if (t.includes('deputy') && (groupType === 'Agencies' || groupType === 'Governor Office')) return true;
    if (t.includes('chief of legislation') && (groupType === 'Agencies' || groupType === 'Governor Office')) return true;
    return false;
}

const getBillDisplay = (bill) => (
    bill.session_number > 0
        ? `${bill.bill_type}x${bill.session_number} ${bill.bill_number}`
        : `${bill.bill_type} ${bill.bill_number}`
);

const getAuthorName = (bill) => {
    const latestVersion = bill.LatestVersion || null;
    if (latestVersion?.Legislator) return latestVersion.Legislator.legislator_name;
    if (latestVersion?.BillVersionAuthors?.length) {
        const primary = latestVersion.BillVersionAuthors.find(a => a.primary_author_flag === 'Y') || latestVersion.BillVersionAuthors[0];
        return primary.author_name;
    }
    if (bill.Author) return `${bill.Author.first_name} ${bill.Author.last_name}`;
    return 'N/A';
};

const OVERVIEW_JSON_LD = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "'elloGovna Overview",
    description: "An overview of 'elloGovna's legislative tracking, staff and lobbyist directory, organization profiles, dashboard, and Workspace tools.",
    url: "https://elloGovna.com/overview",
};

export default function Overview() {
    const [bills, setBills] = useState(null);
    const [billsFailed, setBillsFailed] = useState(false);
    const [featuredHistory, setFeaturedHistory] = useState(null);
    const [headlines, setHeadlines] = useState(null);
    const [headlinesFailed, setHeadlinesFailed] = useState(false);
    const [people, setPeople] = useState(null);
    const [peopleFailed, setPeopleFailed] = useState(false);
    const [orgs, setOrgs] = useState(null);
    const [orgsFailed, setOrgsFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        axios.get('/api/legislation/bills', { params: { limit: 3 } })
            .then((res) => { if (!cancelled) setBills(res.data?.rows || []); })
            .catch(() => { if (!cancelled) setBillsFailed(true); });
        axios.get('/api/news')
            .then((res) => { if (!cancelled) setHeadlines((res.data || []).slice(0, 2)); })
            .catch(() => { if (!cancelled) setHeadlinesFailed(true); });
        axios.get('/api/profiles/directory')
            .then((res) => { if (!cancelled) setPeople(res.data || []); })
            .catch(() => { if (!cancelled) setPeopleFailed(true); });
        axios.get('/api/orgs/', { params: { directory: 'true' } })
            .then((res) => { if (!cancelled) setOrgs(res.data || []); })
            .catch(() => { if (!cancelled) setOrgsFailed(true); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const featured = bills && bills[0];
        if (!featured) return;
        let cancelled = false;
        axios.get(`/api/legislation/bills/${featured.bill_id}/history`)
            .then((res) => { if (!cancelled) setFeaturedHistory(res.data || []); })
            .catch(() => { if (!cancelled) setFeaturedHistory([]); });
        return () => { cancelled = true; };
    }, [bills]);

    const billsLoading = bills === null && !billsFailed;
    const billsLive = !!(bills && bills.length > 0);
    const displayBills = billsLive ? bills : FALLBACK_BILLS;
    const featuredBill = displayBills[0];
    const featuredHistoryToUse = billsLive ? (featuredHistory || []) : FALLBACK_HISTORY;

    const headlinesLoading = headlines === null && !headlinesFailed;
    const headlinesLive = !!(headlines && headlines.length > 0);
    const displayHeadlines = headlinesLive ? headlines : FALLBACK_HEADLINES;

    const peopleLoading = people === null && !peopleFailed;
    const qualifyingPeople = (people || [])
        .map((p) => {
            const jobs = p.JobExperiences || [];
            const job = jobs.find((j) => j.current_job) || jobs[0];
            if (!job) return null;
            const groupType = job.Organization?.group_type;
            if (!personQualifies(job.title, groupType)) return null;
            return {
                profile_id: p.profile_id,
                firstName: p.first_name,
                lastName: p.last_name,
                title: job.title,
                organization: job.Organization?.organization_name,
                division: job.Division?.division_name || '',
                profile_picture: p.profile_picture,
                group_type: groupType,
            };
        })
        .filter(Boolean)
        .slice(0, PEOPLE_LIMIT);
    const peopleLive = qualifyingPeople.length > 0;
    const displayPeople = peopleLive ? qualifyingPeople : SAMPLE_PEOPLE;

    const orgsLoading = orgs === null && !orgsFailed;
    const legislativeOrg = useMemo(() => {
        if (!orgs) return null;
        const pool = orgs.filter((o) => o.logo && ['California State Senate', 'California State Assembly'].includes(o.organization_name));
        if (pool.length === 0) return null;
        return pool[Math.floor(Math.random() * pool.length)];
    }, [orgs]);
    const chamberOrg = (orgs || []).find((o) => o.logo && o.organization_name === 'California Chamber of Commerce');
    const randomOrg = useMemo(() => {
        if (!orgs) return null;
        const pool = orgs.filter((o) => o.logo && o.group_type !== 'Legislative' && o.organization_name !== 'California Chamber of Commerce');
        if (pool.length === 0) return null;
        return pool[Math.floor(Math.random() * pool.length)];
    }, [orgs]);
    const qualifyingOrgs = [legislativeOrg, chamberOrg, randomOrg].filter(Boolean).map((o) => ({
        organization_id: o.organization_id,
        organization_name: o.organization_name,
        street_address: o.street_address,
        suite: o.suite_number,
        city: o.city,
        state: o.state,
        main_phone: o.main_phone,
        logo: o.logo,
        group_type: o.group_type,
    }));
    const orgsLive = qualifyingOrgs.length > 0;
    const displayOrgs = orgsLive ? qualifyingOrgs : SAMPLE_ORGS;

    return (
        <>
            <SEO
                title="Overview | 'elloGovna"
                description="An overview of what's inside 'elloGovna: real-time legislative tracking, a searchable staff and lobbyist directory, organization profiles, a personalized dashboard, and Workspace tools for teams."
                path="/overview"
                jsonLd={OVERVIEW_JSON_LD}
            />

            <section className="overview-hero">
                <div className="overview-container">
                    <p className="overview-hero-eyebrow">Take a Look Inside</p>
                    <h1 className="overview-hero-title">An Overview of <i>'ello</i>Govna</h1>
                    <p className="overview-hero-sub">
                        A tour of the tools California policy professionals use every day — scroll down to explore,
                        with a few real, live examples along the way.
                    </p>
                </div>
            </section>

            {/* ── Legislative Tracking (real data) ─────────────────────── */}
            <section className="overview-section">
                <div className="overview-container">
                    <Reveal>
                        <div className="overview-feature">
                            <div className="overview-feature-text">
                                <p className="overview-feature-eyebrow">Legislative Tracking</p>
                                <h2 className="overview-feature-title">Track Legislation in Real Time</h2>
                                <p className="overview-feature-desc">
                                    Search and filter bills, save the ones you care about, and follow amendments and
                                    status changes as they happen. These are real bills currently moving through the
                                    California Legislature.
                                </p>
                                <DataBadge live={billsLive} />
                            </div>
                            <div className="overview-feature-visual">
                                {billsLoading ? (
                                    <SkeletonCards count={3} />
                                ) : (
                                    displayBills.map((bill) => (
                                        <div key={bill.bill_id} className="card mb-2 bill-card">
                                            <div className="card-body py-2 px-3">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <div>
                                                        <span className="bill-number">{getBillDisplay(bill)}</span>
                                                        <span className="mb-1 text-muted"> - {getAuthorName(bill)}</span>
                                                    </div>
                                                    <i className="bill-tracking-bookmark bi bi-bookmark"></i>
                                                </div>
                                                <div className="mb-2 bill-title">{bill.title}</div>
                                                <div className="small text-muted">
                                                    <span className="fw-bold">{bill.status}</span>
                                                    {bill.location ? `: ${bill.location}` : ''}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ── Bill Profile (real data) ──────────────────────────────── */}
            <section className="overview-section overview-section--alt">
                <div className="overview-container">
                    <Reveal>
                        <div className="overview-feature overview-feature--reverse">
                            <div className="overview-feature-text">
                                <p className="overview-feature-eyebrow">Bill Profiles</p>
                                <h2 className="overview-feature-title">Every Bill's Full Story, in One Place</h2>
                                <p className="overview-feature-desc">
                                    Incorporate key contact information into your bill management system, and see
                                    exactly where a bill stands — author, status, an AI-assisted summary, and the
                                    path it's taken through the Legislature.
                                </p>
                                <DataBadge live={billsLive} />
                            </div>
                            <div className="overview-feature-visual">
                                {billsLoading ? (
                                    <SkeletonCards count={1} />
                                ) : (
                                    <div className="card overview-billprofile-card">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start mb-1">
                                                <span className="bill-number">{getBillDisplay(featuredBill)}</span>
                                                <span className="small fw-bold text-muted">{featuredBill.status}</span>
                                            </div>
                                            <div className="mb-2 bill-title">{featuredBill.title}</div>
                                            <div className="small text-muted mb-3">Author: {getAuthorName(featuredBill)}</div>

                                            {featuredBill.ai_summary && (
                                                <div className="overview-ai-summary mb-3">
                                                    <div className="overview-card-eyebrow"><i className="bi bi-stars me-1" />AI Summary</div>
                                                    <p>{featuredBill.ai_summary}</p>
                                                </div>
                                            )}

                                            <BillLifecycleProgress bill={featuredBill} billHistory={featuredHistoryToUse} />

                                            {featuredHistoryToUse.length > 0 && (
                                                <ul className="overview-history-list mt-3">
                                                    {featuredHistoryToUse.slice(0, 2).map((h) => (
                                                        <li key={h.bill_history_id}>
                                                            <span className="overview-history-date">
                                                                {new Date(h.action_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                            </span>
                                                            {h.action}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ── Staff & Lobbyist Directory (live data) ────────────────── */}
            <section className="overview-section">
                <div className="overview-container">
                    <Reveal>
                        <div className="overview-feature">
                            <div className="overview-feature-text">
                                <p className="overview-feature-eyebrow">Directory</p>
                                <h2 className="overview-feature-title">Find the Right Person, Fast</h2>
                                <p className="overview-feature-desc">
                                    A searchable directory of legislators, committee staff, agency personnel,
                                    advocates, and others working in the Sacramento policymaking community.
                                </p>
                                <DataBadge live={peopleLive} />
                            </div>
                            <div className="overview-feature-visual overview-people-grid">
                                {peopleLoading ? (
                                    <SkeletonCards count={6} />
                                ) : (
                                    displayPeople.map((person) => (
                                        <DirectoryCard key={person.profile_id} {...person} />
                                    ))
                                )}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ── Organization Profiles (live data) ─────────────────────── */}
            <section className="overview-section overview-section--alt">
                <div className="overview-container">
                    <Reveal>
                        <div className="overview-feature overview-feature--reverse">
                            <div className="overview-feature-text">
                                <p className="overview-feature-eyebrow">Organizations</p>
                                <h2 className="overview-feature-title">Every Organization at a Glance</h2>
                                <p className="overview-feature-desc">
                                    Find the organizations you need to reach by name, and see contact info,
                                    leadership, and staff all in one profile.
                                </p>
                                <DataBadge live={orgsLive} />
                            </div>
                            <div className="overview-feature-visual overview-feature-visual--row">
                                {orgsLoading ? (
                                    <SkeletonCards count={2} />
                                ) : (
                                    displayOrgs.map((org) => (
                                        <OrganizationCard key={org.organization_id} {...org} />
                                    ))
                                )}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ── Policy News (real data) ────────────────────────────────── */}
            <section className="overview-section">
                <div className="overview-container">
                    <Reveal>
                        <div className="overview-feature">
                            <div className="overview-feature-text">
                                <p className="overview-feature-eyebrow">Public Square</p>
                                <h2 className="overview-feature-title">Curated Policy News, Every Morning</h2>
                                <p className="overview-feature-desc">
                                    Find and scroll a list of the day's headlines related to California policy
                                    issues — these are real headlines from the last day or two.
                                </p>
                                <DataBadge live={headlinesLive} />
                            </div>
                            <div className="overview-feature-visual">
                                {headlinesLoading ? (
                                    <SkeletonCards count={2} />
                                ) : (
                                    displayHeadlines.map((headline, i) => (
                                        <HeadlineCard key={headline.headline_id || i} {...headline} />
                                    ))
                                )}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ── Public Square shared feed (illustrative) ──────────────── */}
            <section className="overview-section overview-section--alt">
                <div className="overview-container">
                    <Reveal>
                        <div className="overview-feature overview-feature--reverse">
                            <div className="overview-feature-text">
                                <p className="overview-feature-eyebrow">Public Square</p>
                                <h2 className="overview-feature-title">A Shared Feed for the Capitol Community</h2>
                                <p className="overview-feature-desc">
                                    Scroll a shared feed where members post press events, fundraisers, office news,
                                    and other announcements visible to the entire Sacramento policy community.
                                </p>
                                <DataBadge live={false} />
                            </div>
                            <div className="overview-feature-visual">
                                <div className="card overview-post-card">
                                    <div className="card-body">
                                        <div className="d-flex align-items-center mb-2">
                                            <span className="overview-post-avatar">SU</span>
                                            <div className="ms-2">
                                                <div className="fw-bold">Sample User</div>
                                                <div className="small text-muted">Sample Policy Coalition</div>
                                            </div>
                                            <span className="small text-muted ms-auto">2h ago</span>
                                        </div>
                                        <p className="mb-2">
                                            Join us next Thursday for a briefing on this session's sample policy
                                            package — RSVP link in comments!
                                        </p>
                                        <div className="small text-muted">
                                            <i className="bi bi-heart me-1" />12&nbsp;&nbsp;
                                            <i className="bi bi-chat me-1" />3
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ── Dashboard (illustrative) ───────────────────────────────── */}
            <section className="overview-section">
                <div className="overview-container">
                    <Reveal>
                        <div className="overview-feature">
                            <div className="overview-feature-text">
                                <p className="overview-feature-eyebrow">Dashboard</p>
                                <h2 className="overview-feature-title">One Dashboard for Everything You Track</h2>
                                <p className="overview-feature-desc">
                                    Bill activity, outstanding tasks, today's hearings, and top headlines — all
                                    personalized and all in one place when you log in.
                                </p>
                                <DataBadge live={false} />
                            </div>
                            <div className="overview-feature-visual">
                                <div className="dashboard-card mb-3">
                                    <div className="dashboard-card-header">
                                        <i className="bi bi-bar-chart-line-fill"></i>
                                        <h6>Legislative Activity</h6>
                                    </div>
                                    <table className="counts-table">
                                        <thead>
                                            <tr><th>&nbsp;</th><th>AB</th><th>SB</th></tr>
                                        </thead>
                                        <tbody>
                                            <tr><td className="metric-label">Active</td><td>142</td><td>88</td></tr>
                                            <tr><td className="metric-label">Introduced Today</td><td>4</td><td>1</td></tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div className="dashboard-card">
                                    <div className="dashboard-card-header">
                                        <i className="bi bi-briefcase-fill"></i>
                                        <h6>Workspace</h6>
                                    </div>
                                    <div className="workspace-tasks-section">
                                        <div className="workspace-tasks-header">Outstanding Tasks</div>
                                        <ul className="workspace-tasks-list">
                                            <li className="workspace-task-item">
                                                <div className="workspace-task-main">
                                                    <span className="workspace-task-team">Sample Team · AB 000</span>
                                                    <span className="workspace-task-text">Draft support letter</span>
                                                </div>
                                                <div className="workspace-task-meta">
                                                    <span className="workspace-task-due">Mar 4</span>
                                                    <span className="workspace-task-priority task-priority-high">High</span>
                                                </div>
                                            </li>
                                            <li className="workspace-task-item">
                                                <div className="workspace-task-main">
                                                    <span className="workspace-task-team">Sample Team · SB 000</span>
                                                    <span className="workspace-task-text">Confirm hearing witnesses</span>
                                                </div>
                                                <div className="workspace-task-meta">
                                                    <span className="workspace-task-due">Mar 9</span>
                                                    <span className="workspace-task-priority task-priority-medium">Medium</span>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ── Workspace tools (illustrative) ────────────────────────── */}
            <section className="overview-section overview-section--alt">
                <div className="overview-container">
                    <Reveal>
                        <div className="overview-feature-text overview-feature-text--centered mb-4">
                            <p className="overview-feature-eyebrow">Workspace</p>
                            <h2 className="overview-feature-title">Built for Workspace Teams</h2>
                            <p className="overview-feature-desc">
                                A shared account for your team to track bills together, assign work, collect
                                feedback from Clients or Stakeholder Groups, and build reports — all in one place.
                            </p>
                            <DataBadge live={false} />
                        </div>
                        <div className="overview-mini-grid">
                            {WORKSPACE_TOOLS.map((tool) => (
                                <div key={tool.title} className="overview-mini-feature">
                                    <i className={`bi ${tool.icon} overview-mini-feature-icon`} />
                                    <h6>{tool.title}</h6>
                                    <p>{tool.desc}</p>
                                    {tool.preview}
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="overview-cta">
                <div className="overview-container overview-cta-inner">
                    <h2 className="overview-cta-title">Ready to see the real thing?</h2>
                    <p className="overview-cta-body">
                        Create a free account to track real legislation, search the full directory, and follow
                        today's policy news.
                    </p>
                    <button
                        className="overview-btn overview-btn-primary"
                        data-bs-toggle="modal"
                        data-bs-target="#registerModal"
                    >
                        Create Your Account
                    </button>
                </div>
            </section>

            <footer className="overview-footer">
                <span>&copy; <i>'ello</i>Govna {new Date().getFullYear()}</span>
                <a
                    className="overview-footer-social"
                    href="https://www.linkedin.com/company/ellogovna/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="'elloGovna on LinkedIn"
                >
                    <i className="bi bi-linkedin" />
                </a>
            </footer>

            <LoginModal />
            <RegisterModal />
        </>
    );
}

function SkeletonCards({ count }) {
    return (
        <>
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="overview-skeleton-card" />
            ))}
        </>
    );
}
